/**
 * The segment state machine: everything the live workout knows about *one*
 * segment while it is being run.
 *
 * It is split from `tick.ts` on purpose. `tick.ts` owns the cursor — which
 * segment is current, how a measured budget moves it, and what the rest of the
 * app is told about that. This file owns the rules that hold *inside* a segment:
 * when a milestone cue has been earned, where a burst cycle sits, which drill is
 * being called, and what has to be forgotten when the cursor moves on. Those
 * rules are the ones with fire-once latches and modular arithmetic in them, so
 * they are the ones worth testing without a clock at all.
 *
 * Nothing here reads a clock or holds state: every function takes the segment,
 * the plan, and the latches, and returns what the next latches should be.
 */

import {
  BURST_PHASE_LABELS,
  COOLDOWN_DRILL_ROTATION,
  EMPTY_PLAN_SEGMENT_COPY,
  HALFWAY_MINIMUM_SEGMENT_DURATION,
  STRENGTH_DRILL_ROTATION,
  TIMED_TARGET,
  WARMUP_DRILL_ROTATION,
  isBoxingRoundSegment,
  sessionSegmentId,
  sumSegmentDurations,
  workoutIntervalId,
  workoutSectionId,
} from "@ff/domain";
import type {
  BurstPhase,
  FrozenMilestoneCues,
  Milliseconds,
  SessionPlan,
  SessionSegment,
} from "@ff/domain";

// ---------------------------------------------------------------------------
// A plan the engine can actually run
// ---------------------------------------------------------------------------

/**
 * A plan with at least one segment.
 *
 * The engine's cursor is meaningless without one, and "the plan had no segments"
 * is a condition that must be resolved exactly once — at normalization — rather
 * than defended against at every read. Expressing it in the type is what lets
 * {@link segmentAt} return a `SessionSegment` instead of `SessionSegment |
 * undefined` under `noUncheckedIndexedAccess`.
 */
export type NonEmptySegments = readonly [SessionSegment, ...SessionSegment[]];

export interface RunnableSessionPlan extends SessionPlan {
  readonly segments: NonEmptySegments;
}

/**
 * The ids are fixed strings rather than minted ones because this segment is a
 * projection of *nothing* — it has no authored section or interval behind it —
 * and a random id would make two normalizations of the same empty plan produce
 * two different execution ledgers.
 */
export const EMPTY_PLAN_SEGMENT: SessionSegment = Object.freeze({
  id: sessionSegmentId("empty-plan.preparation"),
  kind: "preparation",
  title: EMPTY_PLAN_SEGMENT_COPY.title,
  detail: EMPTY_PLAN_SEGMENT_COPY.detail,
  duration: 0,
  target: TIMED_TARGET,
  role: "phase",
  sectionId: workoutSectionId("empty-plan.section"),
  intervalId: workoutIntervalId("empty-plan.interval"),
});

/**
 * Folds the user's milestone preferences into the plan and repairs anything a
 * decoder could have handed over (F-TR-06).
 *
 * The fold happens **once**, here, and the result is what the session runs for
 * its whole life. That is why `FrozenMilestoneCues` exists: a halfway toggle
 * flipped mid-round would otherwise change a cue timeline the user already heard
 * the start of. `drillChange` and `comboCalls` are not folded in because they
 * gate *announcements* rather than the plan, so they stay on the engine state.
 */
export function normalizeSessionPlan(
  plan: SessionPlan,
  cues: FrozenMilestoneCues,
): RunnableSessionPlan {
  const clamped = plan.segments.map((segment) =>
    segment.duration >= 0 ? segment : { ...segment, duration: 0 },
  );
  const segments: NonEmptySegments =
    clamped.length > 0 && clamped[0] !== undefined
      ? [clamped[0], ...clamped.slice(1)]
      : [EMPTY_PLAN_SEGMENT];

  return {
    ...plan,
    segments,
    totalDuration: sumSegmentDurations(segments),
    halfwayCue: plan.halfwayCue && cues.halfway,
    // A disabled final-ten preference is expressed as "there is no threshold",
    // not as a second flag, so every downstream test is `countdownCue > 0`.
    countdownCue: cues.finalTen ? Math.max(0, plan.countdownCue) : 0,
    drillSwitchInterval: Math.max(0, plan.drillSwitchInterval),
    workBurst: Math.max(0, plan.workBurst),
    burstRest: Math.max(0, plan.burstRest),
  };
}

export function clampSegmentIndex(plan: RunnableSessionPlan, index: number): number {
  const last = plan.segments.length - 1;
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(0, Math.trunc(index)), last);
}

export function segmentAt(plan: RunnableSessionPlan, index: number): SessionSegment {
  return plan.segments[clampSegmentIndex(plan, index)] ?? plan.segments[0];
}

// ---------------------------------------------------------------------------
// Cue latches
// ---------------------------------------------------------------------------

/**
 * Sentinel for "no burst boundary has been reached in this segment".
 *
 * Zero cannot mean that: zero is a real elapsed offset, and a segment that began
 * exactly on a cycle boundary would then suppress its own first cue.
 */
export const NO_BURST_BOUNDARY: Milliseconds = -1;

/**
 * The four latches that make cues fire once (F-TR-03).
 *
 * Halfway and the final countdown are one-shot booleans; the cadence cues latch
 * on the *offset* they last fired at, because their condition can be true for
 * several consecutive reconciliations and the offset is the only thing that
 * distinguishes "still inside the same boundary" from "a new boundary".
 */
export interface SegmentLatches {
  readonly halfwayPlayed: boolean;
  readonly countdownPlayed: boolean;
  readonly lastDrillSwitch: Milliseconds;
  readonly lastBurstBoundary: Milliseconds;
}

export const FRESH_SEGMENT_LATCHES: SegmentLatches = Object.freeze({
  halfwayPlayed: false,
  countdownPlayed: false,
  lastDrillSwitch: 0,
  lastBurstBoundary: NO_BURST_BOUNDARY,
});

/**
 * The latch set a segment resumed part-way through must start from.
 *
 * Restoring a checkpoint into fresh latches would replay every milestone the
 * user already heard — "Halfway" again at 04:00 of a five-minute round. So the
 * latches are *reconstructed* from where the clock actually is: whatever is
 * already behind the cursor counts as played.
 */
export function restoredSegmentLatches(
  plan: SessionPlan,
  segment: SessionSegment,
  remaining: Milliseconds,
): SegmentLatches {
  const elapsed = Math.max(0, segment.duration - remaining);
  const burst = burstCycle(plan, segment);

  let lastBurstBoundary = NO_BURST_BOUNDARY;
  if (burst !== undefined) {
    const cycleStart = Math.floor(elapsed / burst.cycle) * burst.cycle;
    const resetBoundary = cycleStart + plan.workBurst;
    lastBurstBoundary = resetBoundary <= elapsed ? resetBoundary : cycleStart;
  }

  return {
    halfwayPlayed: plan.halfwayCue && remaining <= segment.duration / 2,
    countdownPlayed: plan.countdownCue > 0 && remaining <= plan.countdownCue,
    lastDrillSwitch:
      plan.drillSwitchInterval > 0
        ? Math.floor(elapsed / plan.drillSwitchInterval) * plan.drillSwitchInterval
        : 0,
    lastBurstBoundary,
  };
}

// ---------------------------------------------------------------------------
// Drills
// ---------------------------------------------------------------------------

/**
 * Which drill is being called, and where in the rotation it sits.
 *
 * The index is carried separately from the name because the rotation is modular:
 * two entries could share a name, and re-deriving the position by searching for
 * the name would then jump the rotation backwards.
 */
export interface DrillPosition {
  readonly name: string;
  readonly index: number;
}

/**
 * The scripted rotations are coaching content, not user data — a warm-up that
 * repeated one movement for eight minutes is a worse warm-up. Segments outside
 * the three scripted kinds rotate through a single entry, which is what makes
 * {@link rotatedDrillPosition} a no-op for them rather than a special case at
 * every call site.
 */
export function drillRotation(segment: SessionSegment): readonly string[] {
  switch (segment.kind) {
    case "warmup":
      return WARMUP_DRILL_ROTATION;
    case "strength":
      return STRENGTH_DRILL_ROTATION;
    case "cooldown":
      return COOLDOWN_DRILL_ROTATION;
    default:
      return [segment.drill ?? segment.title];
  }
}

/**
 * An authored drill that appears in the rotation keeps its place in it, so the
 * next rotation continues the script instead of restarting it.
 */
export function initialDrillPosition(segment: SessionSegment): DrillPosition {
  const options = drillRotation(segment);
  if (segment.drill !== undefined) {
    const index = options.indexOf(segment.drill);
    if (index >= 0) return { name: segment.drill, index };
  }
  return { name: options[0] ?? segment.title, index: 0 };
}

export function rotatedDrillPosition(
  segment: SessionSegment,
  position: DrillPosition,
): DrillPosition {
  const options = drillRotation(segment);
  if (options.length === 0) return position;
  const index = (position.index + 1) % options.length;
  return { name: options[index] ?? position.name, index };
}

// ---------------------------------------------------------------------------
// Burst / moving reset
// ---------------------------------------------------------------------------

export interface BurstCycle {
  readonly work: Milliseconds;
  readonly rest: Milliseconds;
  readonly cycle: Milliseconds;
}

/**
 * Round rhythm exists only on a numbered boxing round with **both** halves set
 * (F-TR-15). Half a rhythm is not a rhythm: a work burst with no rest would
 * announce "Work" against itself forever.
 */
export function burstCycle(plan: SessionPlan, segment: SessionSegment): BurstCycle | undefined {
  if (!isBoxingRoundSegment(segment)) return undefined;
  if (plan.workBurst <= 0 || plan.burstRest <= 0) return undefined;
  return { work: plan.workBurst, rest: plan.burstRest, cycle: plan.workBurst + plan.burstRest };
}

export interface BurstStatus {
  readonly phase: BurstPhase;
  readonly label: string;
  /** How far into the current cycle the clock is. */
  readonly position: Milliseconds;
  /** Time left in the current phase — what the badge counts down. */
  readonly remaining: Milliseconds;
}

/**
 * Position is measured from segment elapsed rather than accumulated per cycle so
 * that a catch-up which skipped whole cycles lands in the right phase anyway.
 */
export function burstStatus(
  plan: SessionPlan,
  segment: SessionSegment,
  remaining: Milliseconds,
): BurstStatus | undefined {
  const burst = burstCycle(plan, segment);
  if (burst === undefined) return undefined;
  const elapsed = Math.max(0, segment.duration - remaining);
  const position = elapsed % burst.cycle;
  if (position < burst.work) {
    return {
      phase: "work",
      label: BURST_PHASE_LABELS.work,
      position,
      remaining: burst.work - position,
    };
  }
  return {
    phase: "movingReset",
    label: BURST_PHASE_LABELS.movingReset,
    position,
    remaining: burst.cycle - position,
  };
}

/**
 * The warning window (F-TR-13). Zero is excluded because a finished segment is
 * not urgent, it is over.
 */
export function isFinalTen(plan: SessionPlan, remaining: Milliseconds): boolean {
  return plan.countdownCue > 0 && remaining > 0 && remaining <= plan.countdownCue;
}

// ---------------------------------------------------------------------------
// Cue evaluation
// ---------------------------------------------------------------------------

/**
 * A milestone reached inside one segment, stamped with the offset into the
 * segment at which it was actually reached.
 *
 * The offset is the whole point. A reconciliation that covers 90 s of stalled
 * time still has to say *when* halfway happened, and "when the browser got round
 * to asking" is not an answer — the caller turns this offset into an instant the
 * same way `tick.ts` turns a boundary into one.
 */
export type SegmentCue =
  | { readonly kind: "halfway"; readonly atSegmentElapsed: Milliseconds }
  | { readonly kind: "finalCountdown"; readonly atSegmentElapsed: Milliseconds }
  /** The cadence reached a new call boundary on a boxing round. */
  | { readonly kind: "comboCall"; readonly atSegmentElapsed: Milliseconds }
  | {
      readonly kind: "drillSwitch";
      readonly atSegmentElapsed: Milliseconds;
      readonly drill: string;
    }
  | { readonly kind: "burstWork"; readonly atSegmentElapsed: Milliseconds }
  | { readonly kind: "burstReset"; readonly atSegmentElapsed: Milliseconds };

export interface SegmentCueInput {
  readonly plan: SessionPlan;
  readonly segment: SessionSegment;
  /** Remaining before this slice of measured time was applied. */
  readonly previousRemaining: Milliseconds;
  readonly remaining: Milliseconds;
  readonly latches: SegmentLatches;
  readonly drill: DrillPosition;
}

export interface SegmentCueOutcome {
  readonly cues: readonly SegmentCue[];
  readonly latches: SegmentLatches;
  readonly drill: DrillPosition;
}

/**
 * Evaluates one slice of measured time against a segment and reports every
 * milestone the slice crossed.
 *
 * Whether any of them is *spoken* is not decided here. A reconciliation after a
 * background stall still has to rotate the drill and move the latches — the
 * state must be correct — while playing nothing, because eleven bells at once is
 * not a truthful account of a session the user was not listening to. Separating
 * "what happened" from "what is announced" is what makes that possible.
 */
export function processSegmentCues(input: SegmentCueInput): SegmentCueOutcome {
  const { plan, segment, previousRemaining, remaining } = input;
  const cues: SegmentCue[] = [];
  let latches = input.latches;
  let drill = input.drill;

  const half = segment.duration / 2;
  if (
    plan.halfwayCue &&
    !latches.halfwayPlayed &&
    previousRemaining > half &&
    remaining <= half &&
    segment.duration >= HALFWAY_MINIMUM_SEGMENT_DURATION
  ) {
    latches = { ...latches, halfwayPlayed: true };
    // `ceil` because remaining is whole milliseconds: on an odd duration the
    // first reading that satisfies `remaining <= d/2` is one millisecond past
    // the exact midpoint, and that reading is when the cue was earned.
    cues.push({ kind: "halfway", atSegmentElapsed: Math.ceil(half) });
  }

  if (
    plan.countdownCue > 0 &&
    !latches.countdownPlayed &&
    previousRemaining > plan.countdownCue &&
    remaining <= plan.countdownCue &&
    // A segment no longer than the threshold would open inside its own final
    // countdown, which is a warning about nothing.
    segment.duration > plan.countdownCue
  ) {
    latches = { ...latches, countdownPlayed: true };
    cues.push({ kind: "finalCountdown", atSegmentElapsed: segment.duration - plan.countdownCue });
  }

  const previousElapsed = segment.duration - previousRemaining;
  const elapsed = segment.duration - remaining;

  if (plan.drillSwitchInterval > 0) {
    const boundary =
      Math.floor(elapsed / plan.drillSwitchInterval) * plan.drillSwitchInterval;
    // `> previousElapsed` is what makes this fire on the crossing rather than for
    // every reading that follows it; the latch covers the case where a slice is
    // shorter than a millisecond of progress and the same boundary is seen twice.
    if (boundary > previousElapsed && boundary > 0 && boundary !== latches.lastDrillSwitch) {
      latches = { ...latches, lastDrillSwitch: boundary };
      if (isBoxingRoundSegment(segment)) {
        cues.push({ kind: "comboCall", atSegmentElapsed: boundary });
      } else if (
        segment.kind === "warmup" ||
        segment.kind === "strength" ||
        segment.kind === "cooldown"
      ) {
        // Recovery and standalone drill intervals are deliberately absent: rest
        // and a rep drill must not be interrupted by boxing cadence (F-TR-28).
        drill = rotatedDrillPosition(segment, drill);
        cues.push({ kind: "drillSwitch", atSegmentElapsed: boundary, drill: drill.name });
      }
    }
  }

  const burst = burstCycle(plan, segment);
  if (burst !== undefined) {
    const cycleStart = Math.floor(elapsed / burst.cycle) * burst.cycle;
    const resetBoundary = cycleStart + burst.work;
    // The most recent boundary of either kind. Taking the max is what keeps one
    // latch honest for two alternating events.
    const latestReset = resetBoundary <= elapsed ? resetBoundary : resetBoundary - burst.cycle;
    const boundary = Math.max(latestReset, cycleStart);
    if (boundary > previousElapsed && boundary > 0 && boundary !== latches.lastBurstBoundary) {
      latches = { ...latches, lastBurstBoundary: boundary };
      if (boundary % burst.cycle === burst.work) {
        cues.push({ kind: "burstReset", atSegmentElapsed: boundary });
      } else if (
        // A work boundary landing on a call boundary says nothing the combo call
        // does not already say, and the combo call is the more useful line. The
        // latch still moved, so the suppression costs the next boundary nothing.
        !(plan.drillSwitchInterval > 0 && boundary % plan.drillSwitchInterval === 0)
      ) {
        cues.push({ kind: "burstWork", atSegmentElapsed: boundary });
      }
    }
  }

  return { cues, latches, drill };
}
